import type { TFunction } from 'i18next'
import type { Booking, BookingStatus, NextSlot, PriceMode, ResourceKind, Weekday } from '@/types/bookings'
import { cn } from '@/lib/utils'
import { weekStart as weekStartFrom } from '@/lib/weekStart'

/** What the SDK throws: an Error that carries the HTTP status and the response body. */
export interface ApiError {
  message?: string
  response?: { status?: number; data?: { message?: string; errors?: Array<{ param?: string; message?: string }>; data?: Record<string, unknown> } }
}

export const apiMessage = (err: unknown, fallback: string): string => {
  const e = err as ApiError | null
  return e?.response?.data?.message || e?.message || fallback
}

/** The resource screen's editor tabs (form tabs, PADROES-UI section 9); list filters use the F-182 components. */
export const pill = (active: boolean) =>
  cn(
    'whitespace-nowrap rounded-lg px-4 py-2 text-center text-sm font-medium transition-colors',
    active ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
  )

export const WEEKDAYS: Weekday[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']
/** `0 = Sunday`, as a price rule's `weekday`. */
export const RULE_WEEKDAYS = [1, 2, 3, 4, 5, 6, 0]
export const RESOURCE_KINDS: ResourceKind[] = ['ROOM', 'DESK', 'STATION', 'OTHER']
export const DURATIONS = [30, 60, 90, 120, 180, 240]
/** A booking that still holds the time. */
export const ACTIVE_STATUSES: BookingStatus[] = ['PENDING_APPROVAL', 'PENDING_PAYMENT', 'CONFIRMED']

/* ------------------------------------------------------------------ dates -- */

const safeZone = (timeZone?: string): string | undefined => {
  if (!timeZone) return undefined
  try {
    new Intl.DateTimeFormat('en', { timeZone })
    return timeZone
  } catch {
    return undefined
  }
}

export const formatTime = (iso: string | null | undefined, locale: string, timeZone?: string): string =>
  iso ? new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit', timeZone: safeZone(timeZone) }).format(new Date(iso)) : ''

export const formatDay = (iso: string | null | undefined, locale: string, timeZone?: string): string =>
  iso ? new Intl.DateTimeFormat(locale, { weekday: 'short', day: '2-digit', month: 'short', timeZone: safeZone(timeZone) }).format(new Date(iso)) : ''

/** "Tue, Nov 3" out of a `yyyy-mm-dd` key (the calendar day, no zone involved). */
export const formatDateKey = (key: string, locale: string, long = false): string => {
  const [y, m, d] = key.split('-').map(Number)
  return new Intl.DateTimeFormat(locale, long
    ? { weekday: 'long', day: '2-digit', month: 'long', timeZone: 'UTC' }
    : { weekday: 'short', day: '2-digit', month: 'short', timeZone: 'UTC' }).format(new Date(Date.UTC(y, m - 1, d)))
}

/** `yyyy-mm-dd` of the instant on the community's calendar. */
export const localDateKey = (iso: string | Date, timeZone?: string): string =>
  new Intl.DateTimeFormat('en-CA', { timeZone: safeZone(timeZone) }).format(typeof iso === 'string' ? new Date(iso) : iso)

export const addDays = (key: string, days: number): string => {
  const [y, m, d] = key.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10)
}

/** Monday of the week the day falls in (the agenda's week always starts on Monday; the rule lives in `lib/weekStart`). */
export const weekStart = (key: string): string => weekStartFrom(key, 1)

/** The weekday's name in the user's language (`mon` .. `sun`). */
export const weekdayName = (day: Weekday | number, locale: string, long = true): string => {
  const index = typeof day === 'number' ? day : (WEEKDAYS.indexOf(day) + 1) % 7
  /* 2024-01-07 was a Sunday. */
  return new Intl.DateTimeFormat(locale, { weekday: long ? 'long' : 'short', timeZone: 'UTC' }).format(new Date(Date.UTC(2024, 0, 7 + index)))
}

/** "08:00" out of "2026-11-03T08:00". */
export const clockOf = (local: string): string => local.slice(11, 16)
export const dateOf = (local: string): string => local.slice(0, 10)

export const minutesBetween = (a: string, b: string): number => Math.round((new Date(b).getTime() - new Date(a).getTime()) / 60000)

/** "2h 05min", "45min". */
export const formatMinutes = (minutes: number | null | undefined): string => {
  if (minutes === null || minutes === undefined) return ''
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return h > 0 ? (m ? `${h}h ${String(m).padStart(2, '0')}min` : `${h}h`) : `${m}min`
}

/* ------------------------------------------------------------------ labels -- */

export const statusVariant = (status: BookingStatus, completed: boolean): 'default' | 'success' | 'warning' | 'danger' | 'info' => {
  if (status === 'CONFIRMED') return completed ? 'default' : 'success'
  if (status === 'PENDING_APPROVAL' || status === 'PENDING_PAYMENT') return 'warning'
  if (status === 'REJECTED' || status === 'NO_SHOW') return 'danger'
  return 'default'
}

export const statusLabel = (t: TFunction, booking: Pick<Booking, 'status' | 'completed'>): string =>
  booking.status === 'CONFIRMED' && booking.completed ? t('bookings:status.COMPLETED') : t(`bookings:status.${booking.status}`)

/** "Free", "3 credits"; a price that needs money says so (not payable yet). */
export const costLabel = (t: TFunction, cost: number, mode: PriceMode | undefined, payable = true): string => {
  if (payable === false || mode === 'MONEY' || mode === 'CREDITS_OR_MONEY' && cost === 0) return t('bookings:cost.money')
  if (!cost) return t('bookings:cost.free')
  return t('bookings:cost.credits', { count: cost })
}

/* ------------------------------------------------------------------ errors -- */

export interface BookingConflict {
  /** The time is taken (or the host is busy): `nextSlots` are the free alternatives. */
  nextSlots: NextSlot[] | null
  /** Not enough credits. */
  shortfall: { required: number; balance: number; shortfall: number } | null
  /** Active bookings in the way (a block or a deletion that needs `cancelBookings`). */
  bookings: Booking[] | null
}

/** What a 409 carries in `data`, in one shape. All `null` when the error is not a conflict. */
export const conflictOf = (err: unknown): BookingConflict => {
  const e = err as ApiError | null
  const data = e?.response?.status === 409 ? e.response.data?.data : null
  return {
    nextSlots: Array.isArray(data?.nextSlots) ? (data.nextSlots as NextSlot[]) : null,
    shortfall: typeof data?.shortfall === 'number'
      ? { required: Number(data.required), balance: Number(data.balance), shortfall: data.shortfall }
      : null,
    bookings: Array.isArray(data?.bookings) ? (data.bookings as Booking[]) : null
  }
}

/** `[{ param, message }]` of a 400 into `{ field: message }`. */
export const fieldErrors = (err: unknown): Record<string, string> => {
  const list = (err as ApiError | null)?.response?.data?.errors ?? []
  const out: Record<string, string> = {}
  list.forEach((e) => { if (e.param && e.message && !out[e.param]) out[e.param] = e.message })
  return out
}

/** Saves a calendar file through the browser. */
export const saveBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
}

/** "2026-11-03T14:00": the community's wall clock right now. */
export const nowLocal = (timeZone?: string): string => {
  const now = new Date()
  const time = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: safeZone(timeZone) }).format(now)
  return `${localDateKey(now, timeZone)}T${time}`
}

/** Minutes before the start from which a booking can be started (the API's `EARLY_MINUTES`). */
export const CHECK_IN_EARLY_MINUTES = 10

/**
 * Whether the "start meeting" button makes sense now: a confirmed booking between 10 minutes before the start
 * and the end. The API has the last word (with the release on it closes `graceMinutes` after the start).
 */
export const inCheckInWindow = (booking: Pick<Booking, 'status' | 'startsAt' | 'endsAt'>, now = Date.now()): boolean =>
  booking.status === 'CONFIRMED'
  && now >= new Date(booking.startsAt).getTime() - CHECK_IN_EARLY_MINUTES * 60000
  && now < new Date(booking.endsAt).getTime()

export interface DurationRules {
  slotMinutes?: number | null
  minDurationMinutes?: number | null
  maxDurationMinutes?: number | null
}

/** The durations a resource accepts: minimum to maximum in steps of the slot, plus the current one. */
export const durationOptions = (rules: DurationRules | undefined, current: number): number[] => {
  const step = Math.max(5, rules?.slotMinutes || 30)
  const min = Math.max(step, rules?.minDurationMinutes || step)
  const max = Math.max(min, rules?.maxDurationMinutes || 240)
  const out = new Set<number>([current])
  for (let m = min; m <= max && out.size < 40; m += step) out.add(m)
  return Array.from(out).sort((a, b) => a - b)
}

